import { useEffect } from "react";
import { useNavigate } from "react-router";
import { useSearchParams } from "react-router-dom";
import React from "react";
import { toast } from "sonner";

import { useRegisterController } from "../hooks";
import { cn } from "../../../lib/utils";
import { AuthFormProps } from "../types";

import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { AuthCard, FormError, PasswordInput, AuthLayout } from "../components";

export const Register: React.FC<AuthFormProps> = ({ className }) => {
  const controller = useRegisterController();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const refId = searchParams.get("referringId");

  useEffect(() => {
    if (refId) {
      localStorage.setItem("referringId", refId);
    }
  }, [refId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await controller.register();
    } catch (error) {
      toast.error("Failed to create account. Please try again.");
    }
  };

  return (
    <AuthLayout>
      <AuthCard
        title="Create an account"
        description="Complete the form below to get started with WealthBee"
        className={className}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-4">
            <div className="grid gap-2">
              <Label htmlFor="companyName">Company name</Label>
              <Input
                id="companyName"
                type="text"
                placeholder="Your organization"
                disabled={controller.loading}
                onChange={(e) => controller.setClientName(e.target.value)}
                autoComplete="organization"
                className={cn(
                  controller.formEmailError ? "border-destructive" : ""
                )}
              />
              <FormError message={controller.formEmailError} />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="your@email.com"
                disabled={controller.loading}
                onChange={(e) => controller.setEmail(e.target.value)}
                autoComplete="email"
                className={cn(
                  controller.formEmailError ? "border-destructive" : ""
                )}
              />
              <FormError message={controller.formEmailError} />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstName">First name</Label>
                <Input
                  id="firstName"
                  type="text"
                  placeholder="First name"
                  disabled={controller.loading}
                  onChange={(e) => controller.setFirstName(e.target.value)}
                  autoComplete="given-name"
                  className={cn(
                    controller.formEmailError ? "border-destructive" : ""
                  )}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="lastName">Last name</Label>
                <Input
                  id="lastName"
                  type="text"
                  placeholder="Last name"
                  disabled={controller.loading}
                  onChange={(e) => controller.setLastName(e.target.value)}
                  autoComplete="family-name"
                  className={cn(
                    controller.formEmailError ? "border-destructive" : ""
                  )}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <PasswordInput
                id="password"
                placeholder="Create a password"
                disabled={controller.loading}
                onChange={(e) => controller.setPassword(e.target.value)}
                autoComplete="new-password"
                error={controller.formPasswordError}
              />
              <FormError message={controller.formPasswordError} />
              <p className="text-xs text-muted-foreground">
                Must be between 6 and 100 characters
              </p>
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={controller.loading}
            >
              {controller.loading
                ? "Creating your account..."
                : "Create account"}
            </Button>

            <FormError message={controller.errorMessage} />
          </div>
        </form>
        <div className="mt-4 text-center text-sm">
          Already have an account?{" "}
          <Button
            variant="link"
            className="p-0 h-auto text-sm"
            onClick={() => navigate("/app/login")}
          >
            Sign in
          </Button>
        </div>
      </AuthCard>
    </AuthLayout>
  );
};
